// BOORO: the files of a task in one place. Images become a grid that opens into a viewer
// to page through; every other file keeps its card with preview, player or download.
import { useLingui } from "@lingui/react/macro";
import type { Artifact } from "@rakazo/contracts";
import { isAttachmentImageMimeType } from "@rakazo/contracts";
import { Button, Dialog, DialogClose, DialogContent, DialogTitle } from "@rakazo/ui-web";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type { KeyboardEvent } from "react";
import { useEffect, useRef, useState } from "react";
import type { ArtifactTarget } from "../lib/artifact-open";
import { useArtifactImage } from "../lib/use-artifact-image";
import { ArtifactFileCard } from "./ArtifactFileCard";

export type GalleryItem = { artifactId: string; name: string; mimeType: string; size: number };

export function artifactGalleryItem(artifact: Artifact): GalleryItem {
  return {
    artifactId: artifact.id,
    name: artifact.name,
    mimeType: artifact.mimeType,
    size: artifact.size,
  };
}

export function ArtifactGallery({
  target,
  items,
  imageLimit,
}: {
  target: ArtifactTarget;
  items: readonly GalleryItem[];
  /** Images shown as tiles; the last tile counts the rest. All by default. */
  imageLimit?: number;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const images = items.filter((item) => isAttachmentImageMimeType(item.mimeType));
  const files = items.filter((item) => !isAttachmentImageMimeType(item.mimeType));
  const shown = imageLimit && images.length > imageLimit ? images.slice(0, imageLimit) : images;
  const hidden = images.length - shown.length;
  if (items.length === 0) return null;
  return (
    <div data-testid="artifact-gallery" className="space-y-2">
      {shown.length > 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(88px,1fr))] gap-1.5">
          {shown.map((image, index) => (
            <ImageTile
              key={image.artifactId}
              target={target}
              image={image}
              more={index === shown.length - 1 ? hidden : 0}
              onOpen={() => setOpen(index)}
            />
          ))}
        </div>
      ) : null}
      {files.map((file) => (
        <ArtifactFileCard key={file.artifactId} target={target} {...file} />
      ))}
      {open !== null && images[open] ? (
        <ImageViewer target={target} images={images} index={open} onIndex={setOpen} />
      ) : null}
    </div>
  );
}

function ImageTile({
  target,
  image,
  more,
  onOpen,
}: {
  target: ArtifactTarget;
  image: GalleryItem;
  more: number;
  onOpen: () => void;
}) {
  const tile = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  const src = useArtifactImage(target, image.artifactId, visible);

  useEffect(() => {
    const element = tile.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "320px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <button
      ref={tile}
      type="button"
      title={image.name}
      aria-label={image.name}
      onClick={onOpen}
      className="relative aspect-square overflow-hidden rounded-lg border border-border bg-muted"
    >
      {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : null}
      {more > 0 ? (
        <span className="absolute inset-0 grid place-items-center bg-background/70 text-sm font-medium text-foreground">
          +{more}
        </span>
      ) : null}
    </button>
  );
}

function ImageViewer({
  target,
  images,
  index,
  onIndex,
}: {
  target: ArtifactTarget;
  images: readonly GalleryItem[];
  index: number;
  onIndex: (index: number | null) => void;
}) {
  const { t } = useLingui();
  const image = images[index]!;
  const src = useArtifactImage(target, image.artifactId);
  const count = images.length;
  const step = (by: number) => onIndex((index + by + count) % count);

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onIndex(null))}>
      <DialogContent
        data-testid="artifact-gallery-viewer"
        showCloseButton={false}
        onKeyDown={(event: KeyboardEvent) => {
          if (event.key === "ArrowRight") step(1);
          if (event.key === "ArrowLeft") step(-1);
        }}
        className="w-auto max-w-none gap-2 bg-transparent p-0 ring-0 sm:max-w-none"
      >
        <DialogTitle className="sr-only">{image.name}</DialogTitle>
        <div className="relative grid min-h-48 min-w-48 place-items-center">
          {src ? (
            <img
              src={src}
              alt={image.name}
              className="h-[80vh] max-w-[90vw] rounded-xl object-contain"
            />
          ) : null}
          <DialogClose
            aria-label={t`Close image preview`}
            render={<Button variant="secondary" size="icon-sm" className="absolute end-2 top-2" />}
          >
            <X />
          </DialogClose>
        </div>
        <div className="mx-auto flex max-w-[90vw] items-center gap-2 rounded-full bg-card px-2 py-1 text-xs text-foreground">
          {count > 1 ? (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t`Previous image`}
              onClick={() => step(-1)}
            >
              <ChevronLeft className="rtl:rotate-180" />
            </Button>
          ) : null}
          <span className="min-w-0 truncate" dir="auto">
            {image.name}
          </span>
          {count > 1 ? (
            <>
              <span className="shrink-0 text-muted-foreground tabular-nums">
                {index + 1}/{count}
              </span>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t`Next image`}
                onClick={() => step(1)}
              >
                <ChevronRight className="rtl:rotate-180" />
              </Button>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
